import { z } from "zod";
import type { CustomAttribute } from "./types";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type TFunction = (key: any, ...args: any[]) => string;

const normalizeEmpty = (val: unknown) =>
  val === "" || val === null ? undefined : val;

const isDateStringComplete = (s: string): boolean => {
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
    return !isNaN(new Date(s).getTime());
  }
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) {
    const parts = s.split("/").map(Number);
    const [dd, mm, yyyy] = parts;
    const d = new Date(yyyy, mm - 1, dd);
    return (
      d.getFullYear() === yyyy && d.getMonth() === mm - 1 && d.getDate() === dd
    );
  }
  return false;
};

const datePreprocess = (val: unknown) => {
  const normalized = normalizeEmpty(val);
  if (normalized === undefined) return undefined;
  if (normalized instanceof Date) return normalized;
  if (typeof normalized === "string" && !isDateStringComplete(normalized)) {
    return "INVALID_DATE";
  }
  const date = new Date(normalized as string);
  return isNaN(date.getTime()) ? "INVALID_DATE" : date;
};

type ValidatorOptions = {
  required: boolean;
  requiredMessage: string;
  t: TFunction;
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]*$/;
const PHONE_REGEX = /^(\+[0-9]{1,3}[0-9-]{7,17}|0[0-9-]{7,17})$/;
const EMIRATES_ID_REGEX = /^(784[0-9]{12}|784-[0-9]{4}-[0-9]{7}-[0-9])$/;
const NUMBER_REGEX = /^-?\d*\.?\d+$/;
const POSITIVE_INTEGER_REGEX = /^\d+$/;

const VALIDATOR_SCHEMAS: Record<
  string,
  (opts: ValidatorOptions) => z.ZodTypeAny
> = {
  Email: ({ required, requiredMessage, t }) => {
    const schema = z
      .string({ required_error: requiredMessage })
      .regex(EMAIL_REGEX, t("validation.invalidEmail"));
    return required
      ? schema.min(1, requiredMessage)
      : schema.optional().or(z.literal(""));
  },
  PhoneNumber: ({ required, requiredMessage, t }) => {
    const schema = z
      .string({
        required_error: requiredMessage,
        invalid_type_error: requiredMessage,
      })
      .regex(PHONE_REGEX, t("validation.invalidPhoneNumber"));
    return required
      ? schema.min(1, requiredMessage)
      : schema.optional().or(z.literal(""));
  },
  EmiratesId: ({ required, requiredMessage, t }) => {
    const schema = z
      .string({
        required_error: requiredMessage,
        invalid_type_error: requiredMessage,
      })
      .regex(EMIRATES_ID_REGEX, t("validation.invalidEmiratesId"));
    return required
      ? schema.min(1, requiredMessage)
      : schema.optional().or(z.literal(""));
  },
  Url: ({ required, requiredMessage, t }) => {
    const schema = z
      .string({
        required_error: requiredMessage,
        invalid_type_error: requiredMessage,
      })
      .url(t("validation.invalidUrl"));
    return required
      ? schema.min(1, requiredMessage)
      : schema.optional().or(z.literal(""));
  },
  DateOfBirth: ({ required, requiredMessage, t }) => {
    const schema = z
      .date({
        required_error: requiredMessage,
        invalid_type_error: t("validation.invalidDate"),
      })
      .max(new Date(), t("validation.dateCannotBeFuture"));
    const finalSchema = required ? schema : schema.optional();
    return z.preprocess(datePreprocess, finalSchema);
  },
  FutureDate: ({ required, requiredMessage, t }) => {
    const schema = z
      .date({
        required_error: requiredMessage,
        invalid_type_error: t("validation.invalidDate"),
      })
      .min(new Date(), t("validation.dateMustBeFuture"));
    const finalSchema = required ? schema : schema.optional();
    return z.preprocess(datePreprocess, finalSchema);
  },
  Number: ({ required, requiredMessage, t }) => {
    const schema = z
      .string({
        required_error: requiredMessage,
        invalid_type_error: requiredMessage,
      })
      .regex(NUMBER_REGEX, t("validation.invalidNumber"));
    return required
      ? schema.min(1, requiredMessage)
      : schema.optional().or(z.literal(""));
  },
  PositiveInteger: ({ required, requiredMessage, t }) => {
    const message = t("validation.invalidPositiveInteger");
    const base = z
      .string({
        required_error: requiredMessage,
        invalid_type_error: requiredMessage,
      })
      .regex(POSITIVE_INTEGER_REGEX, message);
    const schema = required
      ? base.min(1, requiredMessage)
      : base.optional().or(z.literal(""));
    return schema.refine((val) => !val || Number(val) > 0, message);
  },
  Date: ({ required, requiredMessage, t }) => {
    const s = z.date({
      required_error: requiredMessage,
      invalid_type_error: t("validation.invalidDate"),
    });
    const finalSchema = required ? s : s.optional();
    return z.preprocess(datePreprocess, finalSchema);
  },
  OptionValue: ({ required, requiredMessage }) => {
    const schema = z.string({ required_error: requiredMessage });
    return required ? schema.min(1, requiredMessage) : schema.optional();
  },
};

export const createCustomAttributesSchema = (
  t: TFunction,
  customAttributes: CustomAttribute[],
) => {
  const schemaFields: Record<string, z.ZodTypeAny> = {};
  const requiredMessage = t("validation.fieldIsRequired");

  for (const attr of customAttributes) {
    if (attr.hidden) continue;

    let field: z.ZodTypeAny;

    if (attr.validator && VALIDATOR_SCHEMAS[attr.validator]) {
      field = VALIDATOR_SCHEMAS[attr.validator]({
        required: !!attr.required,
        requiredMessage,
        t,
      });
    } else if (attr.type === "checkbox") {
      field = z.boolean().optional();
    } else if (attr.type === "date") {
      const dateBase = z.date({
        required_error: requiredMessage,
        invalid_type_error: t("validation.invalidDate"),
      });
      field = attr.required ? dateBase : dateBase.optional();
      field = z.preprocess(datePreprocess, field);
    } else if (attr.type === "number" || attr.type === "decimal") {
      field = createNumericSchema(attr, t, requiredMessage);
    } else {
      const strBase = z.string({
        required_error: requiredMessage,
        invalid_type_error: requiredMessage,
      });
      field = attr.required
        ? strBase.min(1, requiredMessage)
        : strBase.optional().or(z.literal(""));
    }
    schemaFields[attr.name] = field;
  }

  return z.object(schemaFields).passthrough();
};

function createNumericSchema(
  attr: CustomAttribute,
  t: TFunction,
  requiredMsg: string,
) {
  const { min, max } = attr.data?.numberValidation || {};

  return z.preprocess(
    normalizeEmpty,
    z
      .string({ required_error: requiredMsg })
      .optional()
      .refine(
        (val) => {
          if (!val) return !attr.required;
          const num = parseFloat(val.replace(/,/g, ""));
          if (isNaN(num)) return false;
          if (min !== undefined && num < min) return false;
          if (max !== undefined && num > max) return false;
          return true;
        },
        (val) => {
          if (!val && attr.required) return { message: requiredMsg };
          return { message: t("validation.invalidNumber") };
        },
      ),
  );
}
